import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import arrowLeft from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import { walletService } from "../services";
import { useRestaurant } from "../context/RestaurantContext";
import {
  PayoutRequest,
  WalletBalance,
  WalletTransaction,
} from "../utils/types";

type PayoutProps = {
  setIsPayoutOpen: (open: boolean) => void;
};

const formatMoney = (value: number) =>
  `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const statusColor: Record<string, string> = {
  pending: "bg-orange-500",
  approved: "bg-[#22C55E]",
  rejected: "bg-red-500",
};

const Payout = ({ setIsPayoutOpen }: PayoutProps) => {
  const { restaurantProfile } = useRestaurant();
  const [tab, setTab] = useState<"transactions" | "payouts">("transactions");
  const [balance, setBalance] = useState<WalletBalance | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [payouts, setPayouts] = useState<PayoutRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRequest, setShowRequest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState("");
  const [bankDetails, setBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    ifscCode: "",
    bankName: "",
  });

  const loadWallet = async () => {
    setLoading(true);
    try {
      const [balanceRes, txRes, payoutRes]: any[] = await Promise.all([
        walletService.getBalance(),
        walletService.getTransactions({ page: 1, limit: 30 }),
        walletService.getPayoutRequests({ page: 1, limit: 30 }),
      ]);
      if (balanceRes?.status) setBalance(balanceRes.data);
      if (txRes?.status) setTransactions(txRes.data || []);
      if (payoutRes?.status) setPayouts(payoutRes.data || []);
    } catch (error: any) {
      toast.error(error?.message || "Failed to load wallet");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWallet();
  }, []);

  useEffect(() => {
    const details = restaurantProfile?.bankDetails;
    if (details) {
      setBankDetails({
        accountName: details.accountHolderName || details.accountName || "",
        accountNumber: details.accountNumber || "",
        ifscCode: details.ifscCode || "",
        bankName: details.bankName || "",
      });
    }
  }, [restaurantProfile]);

  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(requestedAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    if (balance && amount > balance.balance) {
      return toast.error("Amount cannot exceed available balance");
    }
    if (!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.ifscCode || !bankDetails.bankName) {
      return toast.error("Bank details are required");
    }
    setSubmitting(true);
    try {
      const response: any = await walletService.createPayoutRequest({
        requestedAmount: amount,
        bankDetails,
      });
      if (response.status) {
        toast.success("Payout request submitted");
        setShowRequest(false);
        setRequestedAmount("");
        loadWallet();
      } else {
        toast.error(response.message || "Failed to request payout");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to request payout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300 overflow-y-auto"
      onClick={() => setIsPayoutOpen(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-900 shadow-2xl rounded-l-[20px] overflow-y-auto flex flex-col"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 z-10 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src={arrowLeft}
                alt="back"
                className="w-[36px] h-[36px] rounded-full border cursor-pointer p-2"
                onClick={() => setIsPayoutOpen(false)}
              />
              <h2 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                Payouts
              </h2>
            </div>
            <img src={cancel} alt="close" className="w-6 h-6 cursor-pointer" onClick={() => setIsPayoutOpen(false)} />
          </div>
        </div>

        <div className="flex-1 px-6 py-4 space-y-4">
          <div className="rounded-2xl p-4 bg-[#FFF4E6] dark:bg-gray-800">
            <p className="text-sm text-[#6A6A6A] dark:text-gray-400">Available balance</p>
            <p className="text-[28px] font-semibold text-[#040404] dark:text-gray-200">
              {formatMoney(balance?.balance || 0)}
            </p>
            <div className="flex justify-between mt-3 text-sm">
              <span>Total earned: {formatMoney(balance?.totalEarned || 0)}</span>
              <span>Paid out: {formatMoney(balance?.totalPaidOut || 0)}</span>
            </div>
          </div>

          <div className="flex gap-2 border-b dark:border-gray-700">
            {(["transactions", "payouts"] as const).map((item) => (
              <button
                key={item}
                onClick={() => setTab(item)}
                className={`flex-1 py-2 font-Urbanist font-medium ${
                  tab === item ? "text-[#FF8A00] border-b-2 border-[#FF8A00]" : "text-gray-500"
                }`}
              >
                {item === "transactions" ? "Transactions" : "Payout requests"}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF8A00]"></div>
            </div>
          ) : tab === "transactions" ? (
            transactions.length === 0 ? (
              <p className="text-center text-gray-500 py-10">No wallet transactions yet</p>
            ) : (
              transactions.map((tx) => (
                <div key={tx._id} className="border dark:border-gray-700 rounded-2xl p-4">
                  <div className="flex justify-between">
                    <div>
                      <p className="font-medium text-[#040404] dark:text-gray-200">{tx.description || tx.type}</p>
                      <p className="text-xs text-gray-500">{formatDate(tx.createdAt)}</p>
                    </div>
                    <p className={`font-semibold ${tx.type === "credit" ? "text-green-600" : "text-red-500"}`}>
                      {tx.type === "credit" ? "+" : "-"}
                      {formatMoney(tx.amount)}
                    </p>
                  </div>
                </div>
              ))
            )
          ) : payouts.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No payout requests yet</p>
          ) : (
            payouts.map((payout) => (
              <div key={payout._id} className="bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-[16px] p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-[#040404] dark:text-gray-200 font-Urbanist text-[20px] font-semibold">
                      {formatMoney(payout.requestedAmount)}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {payout.bankDetails?.bankName} · {payout.bankDetails?.accountNumber}
                    </p>
                  </div>
                  <span className={`${statusColor[payout.status] || "bg-gray-400"} text-white text-[12px] font-semibold px-3 py-1 rounded-[6px] uppercase`}>
                    {payout.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500 pt-3 border-t dark:border-gray-700">
                  Requested on {formatDate(payout.createdAt)}
                </p>
              </div>
            ))
          )}
        </div>

        <div className="p-6 pt-0">
          <button
            onClick={() => setShowRequest(true)}
            className="w-full py-4 text-white font-Urbanist text-[16px] font-semibold"
            style={{ borderRadius: 150, background: "#F30" }}
          >
            Request Payout
          </button>
        </div>

        {showRequest && (
          <div className="absolute inset-0 bg-black/40 flex items-end">
            <form
              onSubmit={handleRequestPayout}
              className="w-full bg-white dark:bg-gray-900 rounded-t-3xl p-6 space-y-3"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold dark:text-gray-200">Request payout</h3>
              <input
                type="number"
                min="1"
                placeholder="Amount"
                value={requestedAmount}
                onChange={(e) => setRequestedAmount(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border dark:bg-gray-800 dark:text-gray-200"
              />
              <input className="w-full px-4 py-3 rounded-lg border dark:bg-gray-800 dark:text-gray-200" placeholder="Account name" value={bankDetails.accountName} onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })} />
              <input className="w-full px-4 py-3 rounded-lg border dark:bg-gray-800 dark:text-gray-200" placeholder="Account number" value={bankDetails.accountNumber} onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })} />
              <input className="w-full px-4 py-3 rounded-lg border dark:bg-gray-800 dark:text-gray-200" placeholder="IFSC" value={bankDetails.ifscCode} onChange={(e) => setBankDetails({ ...bankDetails, ifscCode: e.target.value })} />
              <input className="w-full px-4 py-3 rounded-lg border dark:bg-gray-800 dark:text-gray-200" placeholder="Bank name" value={bankDetails.bankName} onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })} />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowRequest(false)} className="flex-1 py-3 rounded-full border">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 py-3 rounded-full text-white bg-[#F30] disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payout;
